// Archivo que maneja los códigos de acción y resultado en el frontend
//
// src/pages/settings/ActionResultCodes.jsx

/**
 * LEGACY COMPONENT
 * This component is kept temporarily for backward compatibility.
 * It represents an old operational activity form and must not be used
 * as the administrative source of truth for codes management.
 *
 * Administrative management must use CodesManagement.jsx.
 * Operational activity registration must use ConsumerSearch.jsx + ActivityForm.jsx.
 */

import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../../services/api'

const ActionResultCodes = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const [actions, setActions] = useState([])
  const [contacts, setContacts] = useState([])
  const [results, setResults] = useState([])

  const [selectedAction, setSelectedAction] = useState('')
  const [selectedContact, setSelectedContact] = useState('')
  const [selectedResult, setSelectedResult] = useState('')
  const [comments, setComments] = useState('')

  const [consumerId, setConsumerId] = useState('')
  const [loadingCatalogs, setLoadingCatalogs] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const consumerIdFromQuery = searchParams.get('consumerId')
    const consumerIdFromState = location.state?.consumerId

    const resolvedConsumerId =
      consumerIdFromState ?? consumerIdFromQuery ?? ''

    setConsumerId(
      resolvedConsumerId !== null && resolvedConsumerId !== undefined
        ? String(resolvedConsumerId)
        : ''
    )
  }, [location.search, location.state])

  useEffect(() => {
    const fetchCatalogs = async () => {
      setLoadingCatalogs(true)
      setError('')

      try {
        const [actionsRes, contactsRes, resultsRes] = await Promise.all([
          api.get('/codes/actions'),
          api.get('/codes/contacts'),
          api.get('/codes/results'),
        ])

        setActions(Array.isArray(actionsRes?.data) ? actionsRes.data : [])
        setContacts(Array.isArray(contactsRes?.data) ? contactsRes.data : [])
        setResults(Array.isArray(resultsRes?.data) ? resultsRes.data : [])
      } catch (err) {
        console.error('[ActionResultCodes] Error loading catalogs:', err)
        setError('No se pudieron cargar los catálogos operativos.')
        setActions([])
        setContacts([])
        setResults([])
      } finally {
        setLoadingCatalogs(false)
      }
    }

    fetchCatalogs()
  }, [])

  const hasConsumerContext = useMemo(() => {
    return consumerId !== '' && !Number.isNaN(Number(consumerId))
  }, [consumerId])

  const isFormValid = useMemo(() => {
    return (
      hasConsumerContext &&
      selectedAction !== '' &&
      selectedContact !== '' &&
      selectedResult !== ''
    )
  }, [hasConsumerContext, selectedAction, selectedContact, selectedResult])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setSuccess('')

    if (!hasConsumerContext) {
      setError(
        'Este módulo legacy requiere un consumerId válido. Usa el flujo de Consumers para registrar la actividad.'
      )
      return
    }

    if (!selectedAction || !selectedContact || !selectedResult) {
      setError('Debes seleccionar acción, contacto y resultado.')
      return
    }

    const payload = {
      type: Number(selectedAction),
      contactId: Number(selectedContact),
      description: Number(selectedResult),
      consumerId: Number(consumerId),
      comments: comments.trim(),
    }

    setSaving(true)

    try {
      await api.post('/activities/register', payload)
      setSuccess('Actividad registrada correctamente.')

      setSelectedAction('')
      setSelectedContact('')
      setSelectedResult('')
      setComments('')
    } catch (err) {
      console.error('[ActionResultCodes] Error saving activity:', err)

      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'No se pudo registrar la actividad.'

      setError(backendMessage)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Nueva actividad
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Módulo legacy de registro operativo. La gestión administrativa de
              catálogos debe realizarse desde Códigos y catálogos.
            </Typography>
          </Box>

          {!hasConsumerContext && (
            <Alert severity="warning">
              Este formulario requiere un <strong>consumerId</strong> válido para
              guardar. Para operar correctamente, abre el flujo desde la vista de
              consumidores.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <TextField
            label="Consumer ID"
            value={consumerId}
            InputProps={{ readOnly: true }}
            helperText={
              hasConsumerContext
                ? 'Contexto del consumidor detectado correctamente.'
                : 'No se detectó contexto de consumidor.'
            }
            fullWidth
          />

          {loadingCatalogs ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2">
                Cargando catálogos operativos...
              </Typography>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="action-label">Acción</InputLabel>
                  <Select
                    labelId="action-label"
                    value={selectedAction}
                    label="Acción"
                    onChange={(e) => setSelectedAction(e.target.value)}
                  >
                    {actions.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="contact-label">Contacto</InputLabel>
                  <Select
                    labelId="contact-label"
                    value={selectedContact}
                    label="Contacto"
                    onChange={(e) => setSelectedContact(e.target.value)}
                  >
                    {contacts.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="result-label">Resultado</InputLabel>
                  <Select
                    labelId="result-label"
                    value={selectedResult}
                    label="Resultado"
                    onChange={(e) => setSelectedResult(e.target.value)}
                  >
                    {results.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Comentarios"
                  multiline
                  minRows={4}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  fullWidth
                />

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  justifyContent="flex-end"
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/settings/controlpanel/codes-management')}
                  >
                    Ir a catálogos
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                  >
                    Regresar
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!isFormValid || saving}
                  >
                    {saving ? 'Guardando...' : 'Guardar actividad'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </Container>
  )
}

export default ActionResultCodes